function normalizePathname(pathname) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function isSamePathname(left, right) {
  return normalizePathname(left) === normalizePathname(right);
}

class CollectionHeroDropdown extends HTMLElement {
  constructor() {
    super();

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onPanelClick = this.onPanelClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.savedScrollY = 0;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.collection-hero__category-trigger');
    this.panel = this.querySelector('.collection-hero__category-panel');
    this.overlay = this.querySelector('.collection-hero__category-overlay');

    if (!this.trigger || !this.panel || !this.overlay) {
      return;
    }

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.panel.addEventListener('click', this.onPanelClick);
    this.overlay.addEventListener('click', this.onOverlayClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.onTriggerClick);
    }

    if (this.panel) {
      this.panel.removeEventListener('click', this.onPanelClick);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.onOverlayClick);
    }

    document.removeEventListener('keydown', this.onKeydown);

    if (this.isOpen) {
      this.unlockScroll();
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  setScrollLockPadding(scrollbarWidth) {
    if (scrollbarWidth <= 0) {
      return;
    }

    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Pad the element that paints the navbar background (.navbar-main), not
    // the transparent .navbar wrapper: a padded wrapper leaves an unpainted
    // gutter on the right where the category panel shows through. Padding is
    // added on top of the element's own padding so its content keeps the same
    // viewport position once the scrollbar is gone.
    if (!this.fixedElements) {
      this.fixedElements = document.querySelectorAll('.navbar-main, .mobile-nav');
    }

    this.fixedElements.forEach((element) => {
      const basePadding = parseFloat(window.getComputedStyle(element).paddingRight) || 0;
      element.style.paddingRight = `${basePadding + scrollbarWidth}px`;
    });
  }

  clearScrollLockPadding() {
    document.body.style.paddingRight = '';

    if (this.fixedElements) {
      this.fixedElements.forEach((element) => {
        element.style.paddingRight = '';
      });
    }
  }

  lockScroll() {
    this.savedScrollY = window.scrollY;
    this.scrollbarWidth = this.getScrollbarWidth();
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.width = '100%';
    this.setScrollLockPadding(this.scrollbarWidth);
    document.body.classList.add('collection-hero--category-open');
  }

  unlockScroll() {
    if (!document.body.classList.contains('collection-hero--category-open')) {
      return;
    }

    const scrollY = this.savedScrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    this.clearScrollLockPadding();
    document.body.classList.remove('collection-hero--category-open');
    window.scrollTo(0, scrollY);
  }

  open() {
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('aria-hidden', 'false');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.lockScroll();
  }

  close() {
    if (!this.isOpen) {
      return;
    }

    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  isCurrentCollectionLink(link) {
    if (!link?.href) {
      return false;
    }

    let targetUrl;

    try {
      targetUrl = new URL(link.href, window.location.href);
    } catch {
      return false;
    }

    if (targetUrl.origin !== window.location.origin) {
      return false;
    }

    return isSamePathname(targetUrl.pathname, window.location.pathname);
  }

  onPanelClick(event) {
    const link = event.target.closest('a.collection-hero__category-link');

    if (link && (link.classList.contains('active') || this.isCurrentCollectionLink(link))) {
      event.preventDefault();
    }
  }

  onOverlayClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
    }
  }

  hasNavbarModalOpen() {
    return [
      'navbar--show-search',
      'navbar--show-megamenu',
      'navbar--show-custom-megamenu',
      'navbar--show-about-megamenu',
      'navbar--show-mobile-menu',
    ].some((className) => document.body.classList.contains(className));
  }

  onKeydown(event) {
    // When a navbar modal is stacked on top, Escape belongs to that modal,
    // not to the dropdown underneath
    if (event.key === 'Escape' && this.isOpen && !this.hasNavbarModalOpen()) {
      this.close();
      this.trigger.focus();
    }
  }
}

customElements.define('collection-hero-dropdown', CollectionHeroDropdown);
